/**
 * features/comms/SmsHub.tsx — Communications → SMS.
 * ------------------------------------------------------------------
 * Four jobs, top to bottom, in the order they are first needed:
 *
 *   1. CONNECT an Orange SMS Botswana account. The client id and secret are
 *      typed here, checked against Orange, and stored sealed on the server
 *      (functions/lib/vault.js) — this screen never reads them back.
 *   2. SEE what is left in its bundles, and the delivery-receipt address to
 *      give Orange's local team.
 *   3. REGISTER schools: which account each sends through, its sender name,
 *      its limits, and TEST MODE with the numbers it may text. Orange's terms
 *      allow a client to text only under its own name, so Acepsle's own
 *      account stays in test mode and a school texts parents through its own.
 *   4. SEND a test text, and read what became of every text.
 *
 * Every text goes through the same rules in functions/sms.js, including the
 * ones typed here: one message (160 plain characters), no texts 22:00–06:00,
 * a daily limit per number, a monthly cap per school.
 * ---------------------------------------------------------------- */
import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, limit, onSnapshot, orderBy, query, setDoc } from 'firebase/firestore';
import {
  AlertCircle, CheckCircle2, Clock, KeyRound, MessageSquareText, RefreshCw, Save, Send, Wallet,
} from 'lucide-react';
import { db, functions } from '@/lib/firebase';
import { Badge, Button, Card, Field, Input, Select, Textarea } from '@/components/ui/primitives';

interface SmsSchool {
  id: string;
  name?: string;
  accountId?: string;
  active?: boolean;
  testMode?: boolean;
  allowList?: string[];
  dailyLimit?: number;
  monthlyCap?: number;
  senderName?: string;
  callbackUrl?: string;
}

interface SmsMessage {
  id: string;
  school?: string;
  to?: string;
  text?: string;
  kind?: string;
  status?: 'queued' | 'sending' | 'sent' | 'delivered' | 'failed' | 'expired';
  error?: string | null;
  providerStatus?: string;
  sentBy?: string;
  createdAt?: string;
  sendAfter?: string;
}

const saveAccount = httpsCallable<
  { accountId: string; label: string; clientId: string; clientSecret: string }, { ok: boolean }
>(functions, 'saveSmsAccount');
const accountStatus = httpsCallable<
  { accountId: string }, { contracts: unknown; error: string | null; receiptUrl: string }
>(functions, 'smsAccountStatus');
const sendTest = httpsCallable<
  { schoolId: string; to: string; text: string },
  { ok: boolean; id: string; status: string; error: string | null; sendAfter: string }
>(functions, 'sendSmsTest');

const TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'brand'> = {
  queued: 'warning', sending: 'warning', sent: 'brand', delivered: 'success', failed: 'danger', expired: 'neutral',
};

/** The length a phone network counts; mirrors functions/lib/smsRules.js. */
const GSM = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXT = '^{}\\[~]|€';
function gsmLength(t: string): number {
  let n = 0;
  for (const ch of t) {
    if (GSM.includes(ch)) n += 1;
    else if (GSM_EXT.includes(ch)) n += 2;
    else return -1;
  }
  return n;
}

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '');

export function SmsHub() {
  const [schools, setSchools] = useState<SmsSchool[]>([]);
  const [messages, setMessages] = useState<SmsMessage[]>([]);

  useEffect(() => onSnapshot(collection(db, 'smsSchools'), (s) => {
    setSchools(s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SmsSchool, 'id'>) })));
  }), []);
  useEffect(() => onSnapshot(
    query(collection(db, 'smsMessages'), orderBy('createdAt', 'desc'), limit(50)),
    (s) => setMessages(s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SmsMessage, 'id'>) }))),
  ), []);

  return (
    <div className="space-y-6">
      <AccountCard />
      <SchoolsCard schools={schools} />
      <TestSendCard schools={schools} />
      <LogCard messages={messages} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AccountCard() {
  const [accountId, setAccountId] = useState('acepsle');
  const [label, setLabel] = useState('Acepsle (Orange SMS Botswana)');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [status, setStatus] = useState<{ contracts: unknown; error: string | null; receiptUrl: string } | null>(null);

  async function connect() {
    setBusy(true);
    setMsg(null);
    try {
      await saveAccount({ accountId, label, clientId, clientSecret });
      setClientId('');
      setClientSecret('');
      setMsg({ ok: true, text: 'Connected. Orange accepted the credentials, and they are stored sealed.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    setBusy(true);
    setMsg(null);
    try {
      setStatus((await accountStatus({ accountId })).data);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <KeyRound size={18} className="text-brand" />
        <h3 className="text-lg font-semibold text-ink dark:text-gray-100">SMS account</h3>
      </div>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
        From Orange Developer → My apps → your app → SMS Botswana. The secret is checked
        with Orange, then stored encrypted on the server; nobody can read it back here.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Account id" hint="Lowercase, e.g. acepsle or alnur">
          <Input value={accountId} onChange={(e) => setAccountId(e.target.value.trim().toLowerCase())} />
        </Field>
        <Field label="Label">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field label="Client id">
          <Input value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Client secret">
          <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="new-password" />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={connect} loading={busy} disabled={!accountId || !clientId || !clientSecret} icon={<Save size={16} />}>
          Connect account
        </Button>
        <Button variant="secondary" onClick={check} loading={busy} disabled={!accountId} icon={<Wallet size={16} />}>
          Balance and receipt address
        </Button>
      </div>
      {msg && (
        <p className={`mt-3 flex items-start gap-2 text-sm ${msg.ok ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>
          {msg.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />} {msg.text}
        </p>
      )}
      {status && (
        <div className="mt-4 space-y-2 text-sm">
          <p className="text-gray-600 dark:text-gray-300">
            Delivery receipt address — give this to Orange&rsquo;s local SMS team:
          </p>
          <code className="block overflow-x-auto rounded-lg bg-gray-100 p-2 text-xs dark:bg-white/5">{status.receiptUrl}</code>
          {status.error
            ? <p className="text-red-600 dark:text-red-300">{status.error}</p>
            : (
              <pre className="max-h-48 overflow-auto rounded-lg bg-gray-100 p-2 text-xs dark:bg-white/5">
                {JSON.stringify(status.contracts, null, 2)}
              </pre>
            )}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function SchoolsCard({ schools }: { schools: SmsSchool[] }) {
  const blank: SmsSchool = { id: '', name: '', accountId: 'acepsle', active: true, testMode: true, allowList: [], dailyLimit: 3, monthlyCap: 500, senderName: '', callbackUrl: '' };
  const [editing, setEditing] = useState<SmsSchool>(blank);
  const [allow, setAllow] = useState('');
  const [msg, setMsg] = useState('');

  function pick(s: SmsSchool) {
    setEditing({ ...blank, ...s });
    setAllow((s.allowList || []).join(', '));
    setMsg('');
  }

  async function save() {
    const id = editing.id.trim().toLowerCase();
    if (!/^[a-z0-9-]{2,40}$/.test(id)) { setMsg('School id: 2–40 lowercase letters, digits or dashes.'); return; }
    if (editing.senderName && !/^[A-Za-z0-9 ]{1,11}$/.test(editing.senderName)) {
      setMsg('Sender name: up to 11 letters, digits or spaces (Orange’s rule).');
      return;
    }
    const { id: _omit, ...rest } = editing;
    await setDoc(doc(db, 'smsSchools', id), {
      ...rest,
      allowList: allow.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean),
      dailyLimit: Number(editing.dailyLimit) || 3,
      monthlyCap: Number(editing.monthlyCap) || 500,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    setMsg(`Saved ${id}.`);
  }

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <MessageSquareText size={18} className="text-brand" />
        <h3 className="text-lg font-semibold text-ink dark:text-gray-100">Schools</h3>
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        {schools.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => pick(s)}
            className={`rounded-full border px-3 py-1 text-sm ${editing.id === s.id ? 'border-brand text-brand' : 'border-black/10 text-gray-600 dark:border-white/10 dark:text-gray-300'}`}
          >
            {s.name || s.id} {s.testMode !== false && <span className="text-xs text-amber-600">· test</span>}
          </button>
        ))}
        <button type="button" onClick={() => pick(blank)} className="rounded-full border border-dashed border-black/20 px-3 py-1 text-sm text-gray-500 dark:border-white/20">
          + New
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="School id" hint="Must match the school portal's bridge id (Al-Nur is alnur)">
          <Input value={editing.id} onChange={(e) => setEditing({ ...editing, id: e.target.value })} />
        </Field>
        <Field label="Name">
          <Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
        </Field>
        <Field label="SMS account id">
          <Input value={editing.accountId || ''} onChange={(e) => setEditing({ ...editing, accountId: e.target.value.trim().toLowerCase() })} />
        </Field>
        <Field label="Sender name" hint="Exactly as Orange approved it; blank uses Orange's default">
          <Input value={editing.senderName || ''} onChange={(e) => setEditing({ ...editing, senderName: e.target.value })} />
        </Field>
        <Field label="Texts per number per day">
          <Input type="number" min={1} max={10} value={editing.dailyLimit ?? 3} onChange={(e) => setEditing({ ...editing, dailyLimit: Number(e.target.value) })} />
        </Field>
        <Field label="Texts per month (cap)">
          <Input type="number" min={1} value={editing.monthlyCap ?? 500} onChange={(e) => setEditing({ ...editing, monthlyCap: Number(e.target.value) })} />
        </Field>
        <Field label="Status callback URL" hint="The school portal's smsStatus address">
          <Input value={editing.callbackUrl || ''} onChange={(e) => setEditing({ ...editing, callbackUrl: e.target.value.trim() })} />
        </Field>
        <Field label="Mode">
          <Select
            value={editing.active === false ? 'off' : editing.testMode === false ? 'live' : 'test'}
            onChange={(e) => setEditing({ ...editing, active: e.target.value !== 'off', testMode: e.target.value !== 'live' })}
          >
            <option value="test">Test — only the numbers below</option>
            <option value="live">Live — anyone the school sends to</option>
            <option value="off">Off</option>
          </Select>
        </Field>
      </div>
      <div className="mt-3">
        <Field label="Test numbers" hint="Comma-separated. Test mode refuses every other number.">
          <Textarea rows={2} value={allow} onChange={(e) => setAllow(e.target.value)} />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} icon={<Save size={16} />} disabled={!editing.id}>Save school</Button>
        {msg && <span className="text-sm text-gray-600 dark:text-gray-300">{msg}</span>}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function TestSendCard({ schools }: { schools: SmsSchool[] }) {
  const [schoolId, setSchoolId] = useState('acepsle');
  const [to, setTo] = useState('');
  const [text, setText] = useState('Acepsle: a test text from the hub. Please ignore.');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const len = useMemo(() => gsmLength(text), [text]);

  async function send() {
    setBusy(true);
    setMsg(null);
    try {
      const r = (await sendTest({ schoolId, to, text })).data;
      setMsg(r.status === 'queued'
        ? { ok: true, text: `Held until ${when(r.sendAfter)} (no texts 22:00–06:00).` }
        : r.status === 'failed'
          ? { ok: false, text: r.error || 'Orange refused it.' }
          : { ok: true, text: 'Sent. The log below updates when Orange reports delivery.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <Send size={18} className="text-brand" />
        <h3 className="text-lg font-semibold text-ink dark:text-gray-100">Send a test</h3>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="As school">
          <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
          </Select>
        </Field>
        <Field label="To" hint="A number on that school's test list">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="71 234 567" />
        </Field>
      </div>
      <div className="mt-3">
        <Field
          label="Message"
          hint={len < 0 ? 'Contains a character a text cannot carry (curly quotes are straightened when sent).' : `${len}/160 — one text`}
        >
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={send} loading={busy} disabled={!to || !text || len > 160} icon={<Send size={16} />}>Send test</Button>
        {msg && (
          <span className={`text-sm ${msg.ok ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>{msg.text}</span>
        )}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function LogCard({ messages }: { messages: SmsMessage[] }) {
  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <RefreshCw size={18} className="text-brand" />
        <h3 className="text-lg font-semibold text-ink dark:text-gray-100">Recent texts</h3>
      </div>
      {messages.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing sent yet.</p>
      ) : (
        <ul className="divide-y divide-black/5 dark:divide-white/10">
          {messages.map((m) => (
            <li key={m.id} className="py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-ink dark:text-gray-100">+{m.to} · {m.school}</span>
                <Badge tone={TONE[m.status || 'queued']}>{m.status}</Badge>
              </div>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{m.text}</p>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <Clock size={12} /> {when(m.createdAt)} · {m.kind} · {m.sentBy}
                {m.status === 'queued' && m.sendAfter ? ` · goes at ${when(m.sendAfter)}` : ''}
                {m.providerStatus ? ` · Orange: ${m.providerStatus}` : ''}
              </p>
              {m.error && m.status !== 'delivered' && <p className="mt-1 text-xs text-red-600 dark:text-red-300">{m.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
